import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export class BTTPdfReport {
    /**
     * Generates and downloads a PDF report.
     * @param {Object} data - The data object.
     */
    static exportToPdf(data: any) {
        const doc = new jsPDF();
        const title = "BTT Report";
        const { worksite, machine } = data;
        let createFilename = new Date().toLocaleDateString() + '_' + machine.id + '_' + (worksite.projectName || 'Project') + '_' + (worksite.worksiteName || 'Worksite') + '_' + (worksite.tunnelName || 'Tunnel') + '_' + (worksite.peg || 'Peg') + '_' + (worksite.screen || 'Screen');
        doc.setFontSize(18);
        doc.text(title, 14, 22);
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);

        let finalY = 35;
        let isFirstSection = true;

        data.selectedSections.forEach((report: string) => {
            if (!isFirstSection) {
                doc.addPage();
                finalY = 20;
            }

            switch (report) {
                case 'Frontpage': finalY = this.AddFrontpageSection(data, doc, finalY); break;
                case 'Status': finalY = this.AddStatusSection(data, doc, finalY); break;
                case 'BoltGraph': finalY = this.AddBoltGraphsSection(data, doc, finalY); break;
                case 'BoltPlacement': finalY = this.AddBoltPlacementSection(data, doc, finalY); break;
                case 'HourCounter': finalY = this.AddHourCounterSection(data, doc, finalY); break;
                case 'Alarms': finalY = this.AddAlarmsSection(data, doc, finalY); break;

                default: break;
            }
            isFirstSection = false;
        });
        doc.save(createFilename + '.pdf');
    }

    static AddHeaderSubtext(data: any, doc: jsPDF, finalY: number) {
        const worksite = data.worksite;
        const machine = data.machine;
        const timeInformation = data.timeInformation;

        const parts0 = [];
        const parts1 = [];
        if (machine && machine.id) {
            parts0.push(`Machine: ${machine.id}`);
        }
        if (worksite) {
            if (worksite.projectName) parts0.push(worksite.projectName);
            if (worksite.tunnelName) parts0.push(`Tunnel: ${worksite.tunnelName}`);
            if (worksite.peg) parts0.push(`Peg: ${worksite.peg}`);
            if (worksite.screen) parts0.push(`Screen: ${worksite.screen}`);
        }
        if (timeInformation) {
            parts1.push(`Start: ${timeInformation.startTime}`);
            parts1.push(`Stop: ${timeInformation.stopTime}`);
            parts1.push(`Duration: ${timeInformation.totalTime}`);
        }
        if (parts0.length > 0 && parts1.length > 0) {
            doc.setFontSize(9);
            doc.setTextColor(100);
            doc.text(parts0.join(' | '), 20, finalY);
            doc.text(parts1.join(' | '), 20, finalY + 5);
            doc.setTextColor(0);
            doc.setFontSize(11);
            return finalY + 10;
        }
        return finalY;
    }

    static AddFrontpageSection(data: any, doc: jsPDF, finalY: number) {
        const worksite = data.worksite;
        const machine = data.machine;
        const timeInformation = data.timeInformation;
        const peginformation = data.peginformation;

        if (worksite || machine || timeInformation) {
            doc.setFontSize(14);
            doc.text("General Information", 14, finalY + 10);
            finalY += 20;
            const tableBody = [];
            if (worksite) {
                tableBody.push([{ content: 'Project Information', colSpan: 2, styles: { halign: 'center', fillColor: [220, 220, 220], fontStyle: 'bold' } }]);
                tableBody.push(['Project Name', worksite.projectName || '-']);
                tableBody.push(['Worksite', worksite.worksiteName || '-']);
                tableBody.push(['Tunnel', worksite.tunnelName || '-']);
                tableBody.push(['Peg', worksite.peg || '-']);
                tableBody.push(['Screen', worksite.screen || '-']);
            }
            if (machine) {
                tableBody.push([{ content: 'Machine Information', colSpan: 2, styles: { halign: 'center', fillColor: [220, 220, 220], fontStyle: 'bold' } }]);
                tableBody.push(['Machine ID', machine.id || '-']);
                tableBody.push(['Machine Type', machine.type || '-']);
            }
            if (timeInformation) {
                tableBody.push([{ content: 'Production Time', colSpan: 2, styles: { halign: 'center', fillColor: [220, 220, 220], fontStyle: 'bold' } }]);
                tableBody.push(['Start Time', timeInformation.startTime ? timeInformation.startTime : '-']);
                tableBody.push(['Stop Time', timeInformation.stopTime ? timeInformation.stopTime: '-']);
                tableBody.push(['Total Duration', timeInformation.totalTime || '-']);
            }

            if (peginformation) {
                tableBody.push([{ content: 'Peg Information \n Showing the lowest and highest peg numbers registered in this session.', colSpan: 2, styles: { halign: 'center', fillColor: [220, 220, 220], fontStyle: 'bold' } }]);
                tableBody.push(['Lowest registered pegnumber:', peginformation.startPegNumber || '-']);
                tableBody.push(['Highest registered pegnumber:', peginformation.endPegNumber || '-']);
            }
            autoTable(doc, {
                startY: finalY,
                body: tableBody,
                theme: 'grid',
                styles: { fontSize: 10 },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 50 },
                    1: { cellWidth: 'auto' }
                }
            });
            finalY = (doc as any).lastAutoTable.finalY;
        }
        return finalY;
    }

    static AddStatusSection(data: any, doc: jsPDF, finalY: number) {
        const statusData = data.statusDataList || data.statusDataList;
        if (statusData && statusData.length > 0) {
            doc.setFontSize(14);
            doc.text("Bolt Status Overview", 14, finalY + 10);
            finalY = this.AddHeaderSubtext(data, doc, finalY + 15);
            finalY += 10;
            const boxWidth = 180;
            const boxHeight = 80;
            let currentX = 14;
            doc.rect(currentX, finalY, boxWidth, boxHeight);
            doc.text("Status overview", currentX, finalY + 5);
            finalY += boxHeight + 5;

            autoTable(doc, {
                startY: finalY + 5,
                head: [['Peg', 'Vault', 'Section', 'Status', 'Start', 'Stop', 'Duration', 'Hold Time', 'Set Point']],
                body: statusData.map((s: any) => [
                    s.pegNumber,
                    s.vault,
                    s.section,
                    s.status,
                    s.startTime ? new Date(s.startTime).toLocaleTimeString() : '-',
                    s.stopTime ? new Date(s.stopTime).toLocaleTimeString() : '-',
                    s.totalTime,
                    s.holdTime,
                    s.tonsSetPoint
                ]),
                theme: 'striped',
                headStyles: { fillColor: [41, 128, 185] },
                styles: { fontSize: 9, halign: 'center' }
            });
            finalY = (doc as any).lastAutoTable.finalY;
        }
        return finalY;
    }

    static AddBoltGraphsSection(data: any, doc: jsPDF, finalY: number): number {
        const boltGraphData = data.boltGraphDataList;
        if (!boltGraphData || boltGraphData.length === 0) return finalY;

        doc.setFontSize(14);
        doc.text("Bolt Graphs", 14, finalY + 10);
        finalY = this.AddHeaderSubtext(data, doc, finalY + 15);
        finalY += 10;

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 14;
        const graphHeight = 60;
        const graphWidth = pageWidth - (margin * 2);

        boltGraphData.forEach((bolt: any) => {
            if (finalY + graphHeight + 20 > pageHeight) {
                doc.addPage();
                finalY = 20;
            }

            doc.setFontSize(10);
            doc.setTextColor(0);
            doc.text(`Peg ${bolt.pegNumber} — ${bolt.description}`, margin, finalY);

            const startY = finalY + 5;
            const endY = startY + graphHeight;
            const startX = margin;
            const endX = margin + graphWidth;

            // Background
            doc.setDrawColor(220);
            doc.setFillColor(250, 250, 250);
            doc.rect(startX, startY, graphWidth, graphHeight, 'FD');

            const dataPoints = bolt.dataPoints;
            if (!dataPoints || dataPoints.length === 0) {
                doc.text("No data", startX + graphWidth / 2, startY + graphHeight / 2, { align: 'center' });
                finalY += graphHeight + 15;
                return;
            }

            const times = dataPoints.map((d: any) => new Date(d.timestamp).getTime());
            const minTime = Math.min(...times);
            const maxTime = Math.max(...times);
            const timeRange = maxTime - minTime || 1;

            const maxExtract = Math.max(...dataPoints.map((d: any) => d.pressSnsrExtractdBar || 0));
            const maxRetract = Math.max(...dataPoints.map((d: any) => d.pressSnsrrRetractdBar || 0));
            const maxLeft = Math.max(maxExtract, maxRetract, 1) * 1.1;

            const maxForce = Math.max(...dataPoints.map((d: any) => d.pullForceKg || 0));
            const maxSetPoint = Math.max(...dataPoints.map((d: any) => d.tonsSetPoint || 0));
            const maxRightRaw = Math.max(maxForce, maxSetPoint, 1);
            const stepRight = 1000;
            const maxRight = Math.ceil(maxRightRaw / stepRight) * stepRight;

            const getX = (t: number) => startX + ((t - minTime) / timeRange) * graphWidth;
            const getYLeft = (v: number) => endY - (v / maxLeft) * graphHeight;
            const getYRight = (v: number) => endY - (v / maxRight) * graphHeight;

            // Grid & Labels
            doc.setFontSize(7);
            doc.setTextColor(100);
            doc.setDrawColor(240);

            // Left axis (bar) — 5 steps
            const stepLeft = maxLeft / 5;
            for (let i = 0; i <= 5; i++) {
                const v = i * stepLeft;
                const y = getYLeft(v);
                doc.line(startX, y, endX, y);
                doc.text(v.toFixed(1), startX - 1, y + 1, { align: 'right' });
            }

            // Right axis (kg) — stepRight increments
            for (let v = 0; v <= maxRight; v += stepRight) {
                const y = getYRight(v);
                if (y < startY) continue;
                doc.text(v.toFixed(0), endX + 1, y + 1, { align: 'left' });
            }

            // X-axis timestamps
            const fmtTime = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            doc.text(fmtTime(new Date(minTime)), startX, endY + 4, { align: 'left' });
            doc.text(fmtTime(new Date(minTime + timeRange / 2)), startX + graphWidth / 2, endY + 4, { align: 'center' });
            doc.text(fmtTime(new Date(maxTime)), endX, endY + 4, { align: 'right' });

            const drawSeries = (key: string, color: [number, number, number], isRight: boolean) => {
                doc.setDrawColor(...color);
                doc.setLineWidth(0.2);
                const step = Math.ceil(dataPoints.length / 1000);
                for (let i = 0; i < dataPoints.length - step; i += step) {
                    const p1 = dataPoints[i];
                    const p2 = dataPoints[i + step];
                    if (!p1 || !p2) continue;
                    const x1 = getX(new Date(p1.timestamp).getTime());
                    const x2 = getX(new Date(p2.timestamp).getTime());
                    const v1 = p1[key] || 0;
                    const v2 = p2[key] || 0;
                    const y1 = isRight ? getYRight(v1) : getYLeft(v1);
                    const y2 = isRight ? getYRight(v2) : getYLeft(v2);
                    doc.line(x1, y1, x2, y2);
                }
            };

            drawSeries('pressSnsrExtractdBar',  [19, 149, 186], false);  // #1395BA
            drawSeries('pressSnsrrRetractdBar', [13, 60, 85],   false);  // #0D3C55
            drawSeries('pullForceKg',           [241, 108, 32], true);   // #F16C20
            drawSeries('tonsSetPoint',          [242, 183, 5],  true);   // #F2B705

            // Legend
            doc.setFontSize(6);
            doc.setFont(doc.getFont().fontName, 'cursive');
            const legendY = startY + 2;
            const legendX = startX + 2;
            const items = [
                { l: 'Extract Pressure', c: [19, 149, 186] as [number, number, number] },
                { l: 'Retract Pressure', c: [13, 60, 85]   as [number, number, number] },
                { l: 'Pull Force',       c: [241, 108, 32]  as [number, number, number] },
                { l: 'Set Point',        c: [242, 183, 5]   as [number, number, number] },
            ];
            items.forEach((item, idx) => {
                const lx = legendX + (idx * 35);
                doc.setFillColor(...item.c);
                doc.rect(lx, legendY, 2, 2, 'F');
                doc.setTextColor(50);
                doc.text(item.l, lx + 3.5, legendY + 1.7);
            });

            finalY += graphHeight + 15;
        });

        return finalY;
    }

    static AddBoltPlacementSection(data: any, doc: jsPDF, finalY: number) {
        const bolts = data.boltGraphDataList;
        if (!bolts || bolts.length === 0) return finalY;

        doc.setFontSize(14);
        doc.text("Bolt Placement Chart", 14, finalY + 10);
        finalY = this.AddHeaderSubtext(data, doc, finalY + 15);
        finalY += 10;

        // 1. Prepare Data
        const pegs = bolts.map((b: any) => b.pegNumber);
        const vaults = bolts.map((b: any) => b.vault);

        const minPeg = Math.min(...pegs);
        const maxPeg = Math.max(...pegs);
        const minVault = Math.min(...vaults);
        const maxVault = Math.max(...vaults);

        const margin = 14;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const graphWidth = pageWidth - (margin * 2);

        // 2. Calculate Scaling and Chunks
        const minScaleX = 8; // Minimum 8mm per peg unit to avoid squashing
        const drawWidth = graphWidth - 25; // Internal padding

        const totalPegSpan = (maxPeg - minPeg) + 1;

        // Determine scaleX and pegsPerChunk
        let scaleX = drawWidth / totalPegSpan;
        let pegsPerChunk = totalPegSpan;

        if (scaleX < minScaleX) {
            scaleX = minScaleX;
            pegsPerChunk = Math.floor(drawWidth / scaleX);
        }

        const scaleY = 5; // 5mm per vault unit

        // 3. Status Map (to determine colors)
        const statusMap = new Map();
        if (data.statusDataList) {
            data.statusDataList.forEach((s: any) => {
                statusMap.set(`${s.pegNumber}_${s.vault}_${s.section}`, s.status);
            });
        }

        let currentY = finalY;

        // 4. Iterate through chunks
        for (let chunkStart = minPeg; chunkStart <= maxPeg; chunkStart += pegsPerChunk) {
            const chunkEnd = Math.min(chunkStart + pegsPerChunk - 1, maxPeg);

            // Calculate height needed for this chunk
            const vaultCount = (maxVault - minVault) + 1;
            const chunkHeight = (vaultCount * scaleY) + 30; // Header + Footer space

            // Check Page Break
            if (currentY + chunkHeight > pageHeight - 20) {
                doc.addPage();
                currentY = 20;
                doc.setFontSize(14);
                doc.text("Bolt Placement Chart (Cont.)", 14, currentY);
                currentY += 10;
            }

            const startX = margin;
            const startY = currentY + 10;

            const getX = (p: number, s: number = 0) => startX + 10 + ((p + s - chunkStart) * scaleX);

            // Draw Vertical Grid (Pegs)
            doc.setDrawColor(220, 220, 220);
            doc.setLineWidth(0.1);
            doc.setFontSize(8);
            doc.setTextColor(100);

            const gridBottom = startY + (vaultCount * scaleY);

            for (let p = chunkStart; p <= chunkEnd; p++) {
                const x = getX(p);
                doc.line(x, startY, x, gridBottom);
                doc.text(p.toString(), x, gridBottom + 5, { align: 'center' });
            }

            // Axis Label
            const midX = startX + 10 + ((chunkEnd - chunkStart) * scaleX) / 2;
            doc.text(`Peg Number (${chunkStart} - ${chunkEnd})`, midX, gridBottom + 12, { align: 'center' });

            // Draw Rows (Vaults)
            let rowY = startY;
            for (let v = minVault; v <= maxVault; v++) {
                // Horizontal Line
                doc.setDrawColor(220, 220, 220);
                doc.line(startX, rowY, startX + graphWidth, rowY);

                // Vault Label
                doc.setTextColor(100);
                doc.text(v.toString(), startX - 2, rowY, { baseline: 'middle', align: 'right' });

                // Bolts
                const boltsInRow = bolts.filter((b: any) =>
                    b.vault === v &&
                    b.pegNumber >= chunkStart &&
                    b.pegNumber <= chunkEnd
                );

                boltsInRow.forEach((bolt: any) => {
                    const x = getX(bolt.pegNumber, bolt.section);

                    let color = [59, 130, 246]; // Active Blue
                    const statusStr = statusMap.get(`${bolt.pegNumber}_${bolt.vault}_${bolt.section}`);

                    if (statusStr) {
                        const s = statusStr.toLowerCase();
                        if (s.includes('reached') || s.includes('complete')) color = [34, 197, 94];
                        else if (s.includes('not finalized') || s.includes('waiting')) color = [245, 158, 11];
                        else if (s.includes('error') || s.includes('stop') || s.includes('fail')) color = [239, 68, 68];
                    }

                    doc.setFillColor(color[0]!, color[1]!, color[2]!);
                    doc.setDrawColor(255, 255, 255);
                    doc.setLineWidth(0.2);
                    doc.circle(x, rowY, 2.5, 'FD');
                });

                rowY += scaleY;
            }

            currentY += chunkHeight + 10;
        }

        // 5. Legend
        if (currentY + 20 > pageHeight) {
            doc.addPage();
            currentY = 20;
        }

        const legendItems = [
            { label: 'Finished', color: [34, 197, 94] },
            { label: 'Pending', color: [245, 158, 11] },
            { label: 'Error', color: [239, 68, 68] },
            { label: 'Active', color: [59, 130, 246] }
        ];

        let legendX = margin;
        legendItems.forEach(item => {
            doc.setFillColor(item.color[0]!, item.color[1]!, item.color[2]!);
            doc.circle(legendX, currentY, 2, 'F');
            doc.setTextColor(0);
            doc.text(item.label, legendX + 4, currentY + 1);
            legendX += 30;
        });

        return currentY + 10;
    }

    static AddHourCounterSection(data: any, doc: jsPDF, finalY: number) {
        const hourCounters = data.hoursDataList;
        if (hourCounters && hourCounters.length > 0) {
            doc.setFontSize(14);
            doc.text("Hour Counters", 14, finalY + 10);
            finalY = this.AddHeaderSubtext(data, doc, finalY + 15);
            finalY += 10;

            const lastItem = hourCounters[hourCounters.length - 1];

            const startX = 14;
            const boxWidth = 100;
            const boxHeight = 25;

            doc.setDrawColor(200);
            doc.setFillColor(250, 250, 250);
            doc.rect(startX, finalY + 5, boxWidth, boxHeight, 'FD');

            doc.setFontSize(10);
            doc.setTextColor(0);

            const timeStr = lastItem.timestamp ? new Date(lastItem.timestamp).toLocaleString() : '-';
            const counterStr = lastItem.hpuMinCounter !== undefined ? lastItem.hpuMinCounter : '0';

            doc.text(`Time: ${timeStr}`, startX + 5, finalY + 15);
            doc.text(`HPU Counter: ${counterStr} min`, startX + 5, finalY + 25);

            finalY += boxHeight + 15;
        }
        return finalY;
    }

    static AddAlarmsSection(data: any, doc: jsPDF, finalY: number) {
        const alarms = data.alarmsDataList;
        if (alarms && alarms.length > 0) {
            doc.setFontSize(14);
            doc.text("Alarms", 14, finalY + 10);
            finalY = this.AddHeaderSubtext(data, doc, finalY + 15);
            finalY += 10;

            autoTable(doc, {
                startY: finalY + 5,
                head: [['Time', 'Code', 'Description']],
                body: alarms.map((a: any) => [
                    a.timestamp ? new Date(a.timestamp).toLocaleString() : '',
                    a.alarmId,
                    a.alarmNames
                ]),
                theme: 'striped',
                headStyles: { fillColor: [231, 76, 60] },
                styles: { fontSize: 9 }
            });
            finalY = (doc as any).lastAutoTable.finalY;
        }
        return finalY;
    }

    // static _styleHeader(sheet) {
    //     sheet.getRow(1).font = { bold: true };
    //     sheet.getRow(1).fill = {
    //         type: 'pattern',
    //         pattern: 'solid',
    //         fgColor: { argb: 'FFCCCCCC' }
    //     };
    // }

    static _triggerDownload(buffer: any, filename: string, mimeType: string) {
        const blob = new Blob([buffer], { type: mimeType });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(link.href);
    }

    static _pumpIdToRgb(pumpId: any) {
        const hue = (pumpId * 60) % 360;
        const s = 0.7;
        const l = 0.5;

        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
        const m = l - c / 2;

        let r = 0, g = 0, b = 0;

        if (0 <= hue && hue < 60) { r = c; g = x; b = 0; }
        else if (60 <= hue && hue < 120) { r = x; g = c; b = 0; }
        else if (120 <= hue && hue < 180) { r = 0; g = c; b = x; }
        else if (180 <= hue && hue < 240) { r = 0; g = x; b = c; }
        else if (240 <= hue && hue < 300) { r = x; g = 0; b = c; }
        else if (300 <= hue && hue < 360) { r = c; g = 0; b = x; }

        return [
            Math.round((r + m) * 255),
            Math.round((g + m) * 255),
            Math.round((b + m) * 255)
        ];
    }

    static _recipeIdToRgb(recipeId: any) {
        const hue = (recipeId * 137.508) % 360;
        const s = 0.7;
        const l = 0.5;

        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
        const m = l - c / 2;

        let r = 0, g = 0, b = 0;

        if (0 <= hue && hue < 60) { r = c; g = x; b = 0; }
        else if (60 <= hue && hue < 120) { r = x; g = c; b = 0; }
        else if (120 <= hue && hue < 180) { r = 0; g = c; b = x; }
        else if (180 <= hue && hue < 240) { r = 0; g = x; b = c; }
        else if (240 <= hue && hue < 300) { r = x; g = 0; b = c; }
        else if (300 <= hue && hue < 360) { r = c; g = 0; b = x; }

        return [
            Math.round((r + m) * 255),
            Math.round((g + m) * 255),
            Math.round((b + m) * 255)
        ];
    }
}